<template>
  <JPageContainer title="고객사 관리" icon="building" description="고객사 정보를 조회하고 관리합니다" :show-help="true" :content-scroll="false" @help="onHelp">

      <!-- ==================== 필터바 ==================== -->
      <JFilterBar
        v-model:collapsed="filterCollapsed"
        v-model:filter-values="filterValues"
        :filter-display="filterDisplay"
        :collapsible="true"
        title="고객사 목록"
      >
        <!-- 액션 버튼 -->
        <template #actions>
          <JButton size="xs" variant="outline" @click="onReset">초기화</JButton>
          <JButton size="xs" styletype="primary" @click="onSearch">조회</JButton>
          <JButton size="xs" styletype="primary" @click="onNew">신규</JButton>
        </template>

        <!-- 필터 필드 - 다중 열 배치 -->
        <template #filters>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            <JFormField
              v-model="filterValues.isActive"
              type="combo"
              label="활성여부"
              :options="activeOptions"
              orientation="horizontal"
              label-width="30%"
            />
            <JFormField
v-model="filterValues.keyword" type="input" label="검색어"
              orientation="horizontal"
              label-width="30%" />
          </div>
        </template>
      </JFilterBar>

      <!-- ==================== 그리드 + 상세 영역 (Resizable) ==================== -->
      <JSplitter
        direction="horizontal"
        :default-size="60"
        :min-size="30"
        :second-min-size="20"
        :second-max-size="60"
        class="flex-1"
      >
        <!-- 좌측: 그리드 -->
        <template #left>
          <div class="h-full overflow-auto bg-background">
            <JGrid
              ref="gridRef"
              :column-defs="columnDefs"
              :row-data="rowData"
              :action-buttons="actionButtons"
              :enable-grouping="true"
              :enable-pivot="true"
              :enable-columns-tool-panel="true"
              row-group-panel-show="always"
              pivot-panel-show="always"
              :group-default-expanded="1"
              :compact-footer="true"
              @row-clicked="onRowClicked"
            />
          </div>
        </template>

        <!-- 우측: 상세 영역 -->
        <template #right>
          <div class="h-full overflow-y-auto bg-muted/30">
            <!-- 선택된 항목이 없을 때: EmptyState -->
            <JEmptyState
              v-if="!detail.code && !isNew"
              variant="simple"
              icon="mousePointerClick"
              title="항목을 선택하거나 신규 버튼을 클릭하세요"
              class="h-full"
            />
            
            <!-- 선택된 항목이 있거나 신규일 때: 상세 폼 -->
            <JCard 
              v-else
              class="h-full" 
              :title="isNew ? '신규 등록' : '상세 정보'"
              variant="elevated"
            >
              <!-- actions 슬롯 (header 우측 버튼) -->
              <template #actions>
                <JButton size="xs" styletype="primary" @click="onSave">저장</JButton>
                <JButton v-if="!isNew" size="xs" variant="destructive" @click="onDelete">삭제</JButton>
                <JButton size="xs" variant="outline" @click="onReset">취소</JButton>
              </template>

            <!-- 2열 그리드: 코드, 이름 -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <JFormField
                v-model="detail.code"
                type="input"
                label="코드"
                :readonly="!isNew"
                placeholder="고객사 코드"
              />
              <JFormField
                v-model="detail.name"
                type="input"
                label="이름"
                placeholder="고객사 이름"
              />
            </div>

            <!-- 2열 그리드: 분류, 활성여부 -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              <JFormField
                v-model="detail.category"
                type="combo"
                label="분류"
                :options="categoryOptions"
              />
              <JFormField
                v-model="detail.isActive"
                type="checkbox"
                label="활성여부"
                inline-label="활성"
              />
            </div>
            <div class="grid grid-cols-1 gap-2 mt-2">
              <!-- 전체 너비: 비고 -->
              <JFormField v-model="detail.remark" type="textarea" label="비고" placeholder="비고" />

            </div>

            </JCard>
          </div>
        </template>
      </JSplitter>
  </JPageContainer> 
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { JCard, JEmptyState } from '@/components/molecules'
import { JFilterBar } from '@/components/organisms'
import { JGrid, JButton, JSplitter, type ActionButton } from '@/components/atoms'
import { JFormField } from '@/components/molecules'
import JPageContainer from '../organisms/JPageContainer.vue'

// ==================== 필터 상태 ====================
const filterCollapsed = ref(false)

// 필터 값 (v-model:filterValues)
const filterValues = ref({
  isActive: '',
  keyword: '',
})

// 필터 표시 설정 (배지에 표시될 라벨 및 값 변환 함수)
const filterDisplay = {
  isActive: {
    label: '활성여부',
    displayValue: (val: unknown) => {
      if (val === 'Y') return 'Y:활성'
      if (val === 'N') return 'N:비활성'
      return ''
    },
  },
  keyword: {
    label: '검색어',
  },
}

// ==================== 옵션 데이터 ====================
const activeOptions = [
  { value: 'Y', label: 'Y:활성' },
  { value: 'N', label: 'N:비활성' },
]

const categoryOptions = [
  { value: 'A', label: 'A등급' },
  { value: 'B', label: 'B등급' },
  { value: 'C', label: 'C등급' },
]

// ==================== 그리드 설정 ====================
const gridRef = ref()

// Mock 데이터 - 실제 백엔드 응답 구조
const rowData = ref([
  { code: 'C001', name: '제이솔루션', category: 'A', isActive: 'Y', remark: '주력 고객사' },
  { code: 'C002', name: 'ABC물류', category: 'B', isActive: 'Y', remark: '' },
  { code: 'C003', name: 'XYZ유통', category: 'A', isActive: 'N', remark: '비활성' },
  { code: 'C004', name: '대한물류', category: 'B', isActive: 'Y', remark: '' },
  { code: 'C005', name: '글로벌이커머스', category: 'A', isActive: 'Y', remark: '신규 고객사' },
])

const columnDefs = ref([
  { 
    field: 'code', 
    headerName: '코드', 
    width: 120,
    enableValue: true,  // 집계 가능 (count)
  },
  { 
    field: 'name', 
    headerName: '이름', 
    width: 200,
    enableValue: true,  // 집계 가능 (count)
  },
  { 
    field: 'category', 
    headerName: '분류', 
    width: 100,
    enableRowGroup: true,  // Row Group으로 사용 가능
    enablePivot: true,     // Pivot Column으로 사용 가능
  },
  {
    field: 'isActive',
    headerName: '활성',
    width: 100,
    cellRenderer: (params: any) => (params.value === 'Y' ? '✓' : ''),
    enableRowGroup: true,  // Row Group으로 사용 가능
    enablePivot: true,     // Pivot Column으로 사용 가능
  },
  { 
    field: 'remark', 
    headerName: '비고', 
    flex: 1,
    enableValue: true,  // 집계 가능 (count)
  },
])

// 행별 액션 버튼
const actionButtons: ActionButton[] = [
  {
    icon: 'pencil',
    label: '수정',
    tooltip: '편집',
    onClick: (rowData: any) => {
      isNew.value = false
      detail.value = {
        code: rowData.code,
        name: rowData.name,
        category: rowData.category,
        isActive: rowData.isActive,
        remark: rowData.remark,
      }
    },
  },
  {
    icon: 'trash2',
    label: '삭제',
    tooltip: '삭제',
    styletype: 'danger',
    onClick: (rowData: any) => {
      if (confirm(`${rowData.name}을(를) 삭제하시겠습니까?`)) {
        console.log('삭제:', rowData.code)
        alert(`삭제되었습니다: ${rowData.name}`)
      }
    },
  },
]

// ==================== 상세 영역 상태 ====================
const isNew = ref(false)
const detail = ref({
  code: '',
  name: '',
  category: '',
  isActive: 'Y',
  remark: '',
})

// ==================== 이벤트 핸들러 ====================

/**
 * 그리드 행 클릭 핸들러
 */
function onRowClicked(event: any) {
  isNew.value = false
  const rowData = event.data

  // 선택된 행 데이터를 상세 영역에 바인딩
  detail.value = {
    code: rowData.code,
    name: rowData.name,
    category: rowData.category,
    isActive: rowData.isActive,
    remark: rowData.remark,
  }
}

/**
 * 신규 버튼 클릭
 */
function onNew() {
  isNew.value = true
  // 상세 영역 초기화
  detail.value = {
    code: '',
    name: '',
    category: '',
    isActive: 'Y',
    remark: '',
  }
}

/**
 * 저장 버튼 클릭
 */
function onSave() {
  console.log('저장:', detail.value)
  // TODO: 실제 구현 시 API 호출로 대체
  // if (isNew.value) {
  //   await api.createCustomer(detail.value)
  // } else {
  //   await api.updateCustomer(detail.value.code, detail.value)
  // }
  // 성공 시 그리드 데이터 갱신
  alert(`저장되었습니다: ${detail.value.name}`)
}

/**
 * 삭제 버튼 클릭
 */
function onDelete() {
  if (confirm(`${detail.value.name}을(를) 삭제하시겠습니까?`)) {
    console.log('삭제:', detail.value.code)
    // TODO: 실제 구현 시 API 호출로 대체
    // await api.deleteCustomer(detail.value.code)
    // 성공 시 그리드에서 해당 행 제거
    alert(`삭제되었습니다: ${detail.value.name}`)
  }
}

/**
 * 초기화 버튼 클릭
 */
function onReset() {
  filterValues.value = {
    isActive: '',
    keyword: '',
  }
  console.log('필터 초기화')
}

/**
 * 조회 버튼 클릭
 */
function onSearch() {
  console.log('조회 조건:', filterValues.value)
  // TODO: 실제 구현 시 API 호출로 대체
  // const result = await api.searchCustomers(filterValues.value)
  // rowData.value = result.data
}

/**
 * 도움말 아이콘 클릭
 */
function onHelp() {
  alert('고객사 관리 페이지 도움말')
}

defineExpose({ gridRef })
</script>
